import { useEffect, useRef, useState } from "react";
import { PanelLeft } from "lucide-react";
import { Sidebar } from "@/components/Sidebar";
import { HelpPage } from "@/components/HelpPage";
import { FeedbackModal } from "@/components/FeedbackModal";
import { Button } from "@/components/ui/Button";
import { WelcomeScreen } from "@/components/WelcomeScreen";
import { MessageList } from "@/components/MessageList";
import { Composer, type ComposerHandle } from "@/components/Composer";
import { Toast } from "@/components/ui/Toast";
import { useChat } from "@/hooks/useChat";
import { useTeamsAuth } from "@/hooks/useTeamsAuth";
import { useBrowserAuth } from "@/hooks/useBrowserAuth";
import { useTheme } from "@/hooks/useTheme";
import { useOnline, isInsideTeams } from "@/hooks/useOnline";
import { useMcpStatus } from "@/hooks/useMcpStatus";
import { ServiceBanner } from "@/components/ServiceBanner";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { fetchStarters } from "@/lib/api";
import { initTelemetry, trackEvent } from "@/lib/telemetry";
import { TeamsTokenContext } from "@/lib/teamsTokenContext";

export default function App() {
  const authState = useBrowserAuth();
  const { token: teamsToken } = useTeamsAuth();

  // Hold the render while checking Easy Auth session in browser.
  // Avoids flashing the UI before the login redirect fires.
  // AuthenticatedApp is a separate component so its hooks are always called
  // consistently — calling hooks after a conditional return violates the Rules
  // of Hooks and causes React to throw on the auth transition.
  if (authState !== "authenticated") return null;

  return <AuthenticatedApp teamsToken={teamsToken} />;
}

function AuthenticatedApp({ teamsToken }: { teamsToken: string | null }) {
  const {
    turns,
    status,
    toolEvents,
    errorMessage,
    conversationId,
    savedConversations,
    sendMessage,
    retryLast,
    abortInFlight,
    reset,
    dismissError,
    loadConversation,
    deleteSavedConversation,
  } = useChat(teamsToken);

  // Theme is host-driven (Teams or system). The hook keeps <html data-theme> in sync.
  useTheme();
  const online = useOnline();
  const mcpStatus = useMcpStatus();
  const [starters, setStarters] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(() => isInsideTeams());
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const composerRef = useRef<ComposerHandle>(null);

  useEffect(() => {
    initTelemetry();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchStarters(undefined, teamsToken)
      .then((items) => {
        if (!cancelled) setStarters(items);
      })
      .catch(() => {
        /* silently ignore — starters are a nice-to-have */
      });
    return () => {
      cancelled = true;
    };
  }, [teamsToken]);

  const focusComposer = () => composerRef.current?.focus();

  useKeyboardShortcuts({
    onFocusComposer: focusComposer,
    onStop: abortInFlight,
    isLoading: status === "loading",
  });

  const handleSend = (text: string, source: "composer" | "starter" | "followup" = "composer") => {
    setDraft("");
    if (source === "starter") trackEvent("starter_clicked", { chars: text.length });
    if (source === "followup") trackEvent("followup_clicked", { chars: text.length });
    void sendMessage(text);
    focusComposer();
  };

  const handleNewChat = () => {
    trackEvent("new_chat_clicked");
    reset();
    setDraft("");
    focusComposer();
  };

  const handleLoadConversation = (conv: Parameters<typeof loadConversation>[0]) => {
    loadConversation(conv);
    setDraft("");
    focusComposer();
  };

  const showWelcome = turns.length === 0;
  const isLoading = status === "loading";

  // Rendered as an in-app view swap (not a real navigation) so it works
  // reliably inside the Microsoft Teams tab iframe, which does not handle
  // full-page navigations to a different path well.
  if (helpOpen) {
    return <HelpPage onBack={() => setHelpOpen(false)} />;
  }

  return (
    <TeamsTokenContext.Provider value={teamsToken}>
    {/* bg-bg canvas with padding so the sidebar card has breathing room on all sides */}
    <div className="flex h-screen overflow-hidden bg-bg p-3 gap-3">
      {/* Collapsible left sidebar — floating panel with rounded right edge */}
      <Sidebar
        isOpen={sidebarOpen}
        currentConversationId={conversationId}
        savedConversations={savedConversations}
        onNewChat={handleNewChat}
        onToggleSidebar={() => setSidebarOpen((o) => !o)}
        onLoadConversation={handleLoadConversation}
        onDeleteConversation={deleteSavedConversation}
        onHelp={() => setHelpOpen(true)}
        onFeedback={() => setFeedbackOpen(true)}
      />

      {/* Main content column — sits on the canvas, no top bar */}
      <div className="relative flex min-w-0 flex-1 flex-col">

        {/* Floating open button — only shown when the sidebar is closed */}
        {!sidebarOpen && (
          <div className="absolute left-0 top-0 z-30 px-3 pt-3 pointer-events-none">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
              title="Open navigation"
              className="pointer-events-auto h-8 w-8 bg-surface/80 backdrop-blur-sm shadow-sm hover:bg-surface"
            >
              <PanelLeft className="h-[16px] w-[16px]" />
            </Button>
          </div>
        )}

        {!online && <ServiceBanner reason="offline" />}
        {online && mcpStatus === "degraded" && <ServiceBanner reason="mcp" />}

        <main
          id="main"
          className="w-full flex-1 overflow-y-auto pb-6 pt-6 max-[680px]:pt-4"
        >
          <div className="mx-auto w-[min(820px,calc(100%-32px))] max-[680px]:w-[calc(100%-24px)]">
            {showWelcome ? (
              <WelcomeScreen
                starters={starters}
                onPick={(t) => handleSend(t, "starter")}
                composerSlot={
                  <Composer
                    ref={composerRef}
                    value={draft}
                    onChange={setDraft}
                    onSubmit={handleSend}
                    onStop={abortInFlight}
                    loading={isLoading}
                    className="pb-0 max-[680px]:pb-0"
                  />
                }
              />
            ) : (
              <MessageList
                turns={turns}
                toolEvents={toolEvents}
                onRegenerate={isLoading ? undefined : retryLast}
                onFollowUp={(q) => handleSend(q, "followup")}
                onCopyError={() => undefined}
              />
            )}
          </div>
        </main>

        {/* When a conversation is active the composer docks to the bottom; on the
            welcome screen it is rendered inline (centered) via the slot above. */}
        {!showWelcome ? (
          <Composer
            ref={composerRef}
            value={draft}
            onChange={setDraft}
            onSubmit={handleSend}
            onStop={abortInFlight}
            loading={isLoading}
          />
        ) : null}
      </div>

      <Toast
        open={!!errorMessage}
        message={errorMessage}
        onDismiss={dismissError}
        variant="error"
      />

      <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
    </TeamsTokenContext.Provider>
  );
}
